import { Card, CardBody } from "@datapay-by-datacom/design-system";
import type { ReturningUsersResponse, ReturningOrgsResponse } from "../../types";
import { formatNumber, formatPercentage } from "../../utils/formatters";
import styles from "./RetentionCards.module.css";

interface RetentionGaugeProps {
  rate: number | null | undefined;
  size?: number;
}

function RetentionGauge({ rate, size = 80 }: RetentionGaugeProps) {
  const value = rate ?? 0;
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(value / 100, 0), 1);
  const strokeDashoffset = circumference * (1 - progress);
  const center = size / 2;

  return (
    <div className={styles.gauge}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--colours-neutral-100, #f3f4f6)"
          strokeWidth={6}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={rate != null ? "#10b981" : "var(--colours-neutral-200, #e5e7eb)"}
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <span className={styles.gaugeLabel}>
        {rate != null ? formatPercentage(rate) : "N/A"}
      </span>
    </div>
  );
}

interface KpiRowProps {
  label: string;
  value: number;
  accent?: string;
}

function KpiRow({ label, value, accent }: KpiRowProps) {
  return (
    <div className={styles.kpiRow}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValue} style={accent ? { color: accent } : undefined}>
        {formatNumber(value)}
      </span>
    </div>
  );
}

export interface RetentionCardsProps {
  variant: "users" | "orgs";
  data: ReturningUsersResponse | ReturningOrgsResponse;
}

export function RetentionCards({ variant, data }: RetentionCardsProps) {
  if (variant === "users") {
    const d = data as ReturningUsersResponse;
    return (
      <div className={styles.container}>
        <div className={styles.gaugeSection}>
          <RetentionGauge rate={d.retention_rate != null ? d.retention_rate * 100 : null} />
          <span className={styles.gaugeCaption}>Retention Rate</span>
        </div>
        <div className={styles.kpiSection}>
          <Card>
            <CardBody>
              <KpiRow label="Monthly Active Users" value={d.mau} accent="#6366f1" />
              <KpiRow label="Monthly Recurring Users" value={d.mru} accent="#8b5cf6" />
              <KpiRow label="New Users" value={d.new_users} />
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  const d = data as ReturningOrgsResponse;
  return (
    <div className={styles.container}>
      <div className={styles.gaugeSection}>
        <RetentionGauge rate={d.retention_rate != null ? d.retention_rate * 100 : null} />
        <span className={styles.gaugeCaption}>Retention Rate</span>
      </div>
      <div className={styles.kpiSection}>
        <Card>
          <CardBody>
            <KpiRow label="Active Organisations" value={d.active_orgs} accent="#6366f1" />
            <KpiRow label="Monthly Recurring Orgs" value={d.mro} accent="#8b5cf6" />
            <KpiRow label="New Organisations" value={d.new_orgs} />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
